/**
 * /samples — gan nhan mau, TOI UU CHO TOC DO chu khong cho giao dien dep (muc 12).
 *
 * Muc tieu cua dac ta la >= 10 mau/phut, tuc 6 giay moi mau. O toc do do, moi lan phai
 * dua tay sang chuot la mat 2-3 giay — nen ca man hinh nay duoc thiet ke quanh MOT quy
 * tac: tay khong roi ban phim.
 *
 *   go nhan -> Enter  : luu va sang anh tiep theo
 *   Tab / mui ten     : bo qua, chua muon quyet
 *   Shift+Enter       : danh dau mau hong (rejected) roi sang tiep
 *   Backspace o o rong: quay lai anh truoc de sua
 *
 * Va mot quyet dinh nua ve toc do: LUU NEN. Nguoi dung khong phai cho mang moi anh —
 * anh tiep theo hien ra ngay, con request luu chay phia sau. Neu no that bai thi bao
 * len o loi va mau do quay lai hang doi, chu khong chan tay nguoi dang go.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Paginated, SampleDto } from '@capforge/shared';
import { ErrorBox } from '../components/ErrorBox';
import { Spinner } from '../components/Spinner';
import { api, errorMessage } from '../lib/api';
import { useSampleImage } from '../lib/useSampleImage';

/** Nap them anh khi hang doi con duoi muc nay — de nguoi go khong bao gio phai cho. */
const PREFETCH_BELOW = 8;
const PAGE_SIZE = 40;

interface Pending {
  id: number;
  label: string;
  rejected: boolean;
}

export function Samples(): JSX.Element {
  const [queue, setQueue] = useState<SampleDto[]>([]);
  const [done, setDone] = useState<SampleDto[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [exhausted, setExhausted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [savedCount, setSavedCount] = useState(0);
  const [startedAt] = useState(() => Date.now());
  const [profileFilter, setProfileFilter] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);
  const current = queue[0] ?? null;

  const loadMore = useCallback(
    async (reset = false) => {
      setLoading(true);
      setError(null);
      try {
        const page: Paginated<SampleDto> = await api.samples({
          limit: PAGE_SIZE,
          cursor: reset ? null : cursor,
          // Chi lay mau CHUA kiem chung: mau da co nhan nguoi that thi khong can go lai.
          verified: false,
          profile: profileFilter || undefined,
        });
        setQueue((q) => (reset ? page.items : [...q, ...page.items]));
        setCursor(page.nextCursor ?? null);
        if (page.items.length === 0 || page.nextCursor === null) setExhausted(true);
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [cursor, profileFilter],
  );

  useEffect(() => {
    void loadMore(true);
    // Chi chay lai khi doi bo loc — `loadMore` doi theo cursor nen khong dua vao day.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileFilter]);

  useEffect(() => {
    if (!exhausted && queue.length < PREFETCH_BELOW && !loading) void loadMore();
  }, [queue.length, exhausted, loading, loadMore]);

  // O nhap phai luon giu focus: chi can mot lan mat focus la nguoi dung go vao hu khong
  // va tuong minh da gan nhan xong mot anh.
  useEffect(() => {
    inputRef.current?.focus();
  }, [current?.id]);

  const save = useCallback((pending: Pending) => {
    // Luu NEN: khong await o day, de anh tiep theo hien ra ngay.
    void api
      .labelSample(pending.id, {
        label: pending.label,
        verified: !pending.rejected,
        rejected: pending.rejected,
      })
      .then(() => {
        setSavedCount((n) => n + 1);
      })
      .catch((err: unknown) => {
        setSaveError(`Khong luu duoc mau #${pending.id}: ${errorMessage(err)}`);
      });
  }, []);

  const advance = useCallback(
    (pending: Pending | null) => {
      if (pending) save(pending);
      setQueue((q) => {
        const [head, ...rest] = q;
        if (head) setDone((d) => [head, ...d].slice(0, 20));
        return rest;
      });
      setDraft('');
    },
    [save],
  );

  const goBack = useCallback(() => {
    setDone((d) => {
      const [last, ...rest] = d;
      if (!last) return d;
      setQueue((q) => [last, ...q]);
      setDraft(last.label ?? '');
      return rest;
    });
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!current) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        // Shift+Enter = mau hong (anh khong doc duoc, cat sai...). Danh dau `rejected`
        // thay vi gan nhan bua: mot nhan sai lan vao tap train con hai hon la bo mau do.
        if (e.shiftKey) {
          advance({ id: current.id, label: draft.trim(), rejected: true });
          return;
        }
        if (draft.trim().length === 0) return;
        advance({ id: current.id, label: draft.trim(), rejected: false });
        return;
      }
      if (e.key === 'Tab' || e.key === 'ArrowRight') {
        e.preventDefault();
        advance(null); // bo qua, chua quyet
        return;
      }
      if ((e.key === 'Backspace' && draft.length === 0) || e.key === 'ArrowLeft') {
        e.preventDefault();
        goBack();
      }
    },
    [advance, current, draft, goBack],
  );

  const rate = useMemo(() => {
    const minutes = (Date.now() - startedAt) / 60000;
    return minutes > 0.05 ? savedCount / minutes : 0;
  }, [savedCount, startedAt]);

  const imageUrl = useSampleImage(current?.id ?? null);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Gán nhãn mẫu</h1>
          <p className="max-w-2xl text-sm text-slate-500 dark:text-slate-400">
            Go nhan roi <kbd className="rounded border px-1">Enter</kbd>. Tay khong can roi
            ban phim — do la thu quyet dinh toc do, khong phai giao dien.
          </p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-slate-500">Profile</span>
            <input
              value={profileFilter}
              onChange={(e) => setProfileFilter(e.target.value)}
              placeholder="tat ca"
              className="w-40 rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800"
            />
          </label>
        </div>
      </header>

      {error && <ErrorBox message={error} onRetry={() => void loadMore(true)} />}
      {saveError && <ErrorBox message={saveError} />}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
        <section className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
          {loading && queue.length === 0 ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : current === null ? (
            <div className="py-16 text-center text-slate-500">
              <p className="text-lg">Hết mẫu chưa gán nhãn.</p>
              <p className="mt-1 text-sm">
                Mau moi se xuat hien o day khi co request captcha di qua capforge.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex min-h-[180px] items-center justify-center rounded bg-slate-100 p-4 dark:bg-slate-800">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={`mau ${current.id}`}
                    className="max-h-64 max-w-full"
                    // Anh hong / da bi don theo tran dung luong: noi that thay vi hien o trong.
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : null}
                {current.prompt && (
                  <p className="px-4 text-center text-lg font-medium">{current.prompt}</p>
                )}
              </div>

              <input
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                onBlur={() => inputRef.current?.focus()}
                autoComplete="off"
                spellCheck={false}
                placeholder="gõ đáp án rồi Enter"
                className="w-full rounded border-2 border-blue-500 px-4 py-3 text-center text-2xl tracking-widest focus:outline-none dark:bg-slate-800"
              />

              <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
                <div>
                  <dt className="inline">Mẫu </dt>
                  <dd className="inline font-mono">#{current.id}</dd>
                </div>
                <div>
                  <dt className="inline">Loại </dt>
                  <dd className="inline font-mono">{current.taskType}</dd>
                </div>
                <div>
                  <dt className="inline">Profile </dt>
                  <dd className="inline font-mono">{current.profileKey ?? '—'}</dd>
                </div>
                {current.width && (
                  <div>
                    <dt className="inline">Kich thuoc </dt>
                    <dd className="inline font-mono">
                      {current.width}x{current.height}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </section>

        <aside className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm dark:border-slate-700 dark:bg-slate-900">
            <h2 className="mb-2 font-semibold">Tiến độ</h2>
            <p className="text-3xl font-semibold">{savedCount}</p>
            <p className="text-xs text-slate-500">mau da gan nhan trong phien nay</p>
            <p className="mt-2 text-sm">
              <span className={rate >= 10 ? 'text-emerald-600' : 'text-slate-600'}>
                {rate.toFixed(1)} mau/phut
              </span>
              <span className="text-xs text-slate-500"> — dac ta dat muc tieu 10</span>
            </p>
            <p className="mt-2 text-xs text-slate-500">Con trong hang doi: {queue.length}</p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4 text-xs dark:border-slate-700 dark:bg-slate-900">
            <h2 className="mb-2 text-sm font-semibold">Phím tắt</h2>
            <ul className="space-y-1 text-slate-600 dark:text-slate-400">
              <li>
                <kbd className="rounded border px-1">Enter</kbd> luu va sang anh tiep
              </li>
              <li>
                <kbd className="rounded border px-1">Shift</kbd>+
                <kbd className="rounded border px-1">Enter</kbd> danh dau mau hong
              </li>
              <li>
                <kbd className="rounded border px-1">Tab</kbd> bo qua, chua quyet
              </li>
              <li>
                <kbd className="rounded border px-1">Backspace</kbd> (o rong) quay lai anh truoc
              </li>
            </ul>
          </div>

          {done.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-4 text-xs dark:border-slate-700 dark:bg-slate-900">
              <h2 className="mb-2 text-sm font-semibold">Vừa gán</h2>
              <ul className="space-y-1 font-mono text-slate-600 dark:text-slate-400">
                {done.slice(0, 8).map((s) => (
                  <li key={s.id}>#{s.id}</li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
